/**
 * Responsibility: the two collapsible breakdowns - by category, and by grade.
 *
 * Collapsed by default. They are long, and the question they answer is a
 * second one: the row of libraries above says how much there is, these say
 * what it is made of.
 *
 * **Examples and measurements are different things and the gap is the
 * point.** An example is a picture. A measurement is a picture with a
 * grader's own numbers beside it, and only the second can calibrate
 * anything. Showing examples alone would suggest the calibration set is
 * fifty times larger than it is, so both are columns here and the second is
 * never omitted for being small.
 *
 * **Confidence is shown, not hidden.** A category read out of a seller's
 * title can be wrong; one the grader published cannot. They are not the
 * same claim and the table does not pretend they are.
 */
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import type { DataHubCategory, DataHubGrade } from '../../api/client';
import { CONFIDENCE_COLOUR, CONFIDENCE_LABEL, stamp } from './dataHubFormat';

type BreakdownProps = {
  categories: DataHubCategory[];
  grades: DataHubGrade[];
};

function CategoryTable({ categories }: { categories: DataHubCategory[] }) {
  // Descending, so the sources worth calibrating against are read first.
  const rows = [...categories].sort((left, right) => right.examples - left.examples);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ pl: 0 }}>Category</TableCell>
          <TableCell>Told by</TableCell>
          <TableCell align="right">Examples</TableCell>
          <TableCell align="right">Measured</TableCell>
          <TableCell align="right" sx={{ pr: 0 }}>
            Last updated
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={`${row.category}-${row.confidence}`}>
            <TableCell sx={{ pl: 0 }}>{row.category}</TableCell>
            <TableCell>
              {/* Only `from_title` gets a colour. It is the one that can be
                  wrong - a category read out of a seller's listing - where
                  `unknown` is merely absent and `published` came from the
                  grader. Colouring all three would say they carry the same
                  risk. */}
              <Chip
                size="small"
                variant="outlined"
                color={CONFIDENCE_COLOUR[row.confidence] ?? 'default'}
                label={CONFIDENCE_LABEL[row.confidence] ?? row.confidence}
              />
            </TableCell>
            <TableCell align="right">{row.examples.toLocaleString()}</TableCell>
            <TableCell align="right">
              <Typography
                variant="body2"
                color={row.with_measurement === 0 ? 'text.disabled' : 'text.primary'}
              >
                {row.with_measurement.toLocaleString()}
              </Typography>
            </TableCell>
            <TableCell align="right" sx={{ pr: 0, whiteSpace: 'nowrap' }}>
              <Typography variant="body2" color="text.secondary">
                {stamp(row.last_updated)}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function GradeTable({ grades }: { grades: DataHubGrade[] }) {
  // Grouped by source, because a 9.5 from one grader is not a 9.5 from
  // another and adding them together would invent a scale nobody uses.
  const bySource = new Map<string, DataHubGrade[]>();
  for (const row of grades) {
    bySource.set(row.source, [...(bySource.get(row.source) ?? []), row]);
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ pl: 0 }}>Source</TableCell>
          <TableCell>Grade</TableCell>
          <TableCell align="right" sx={{ pr: 0 }}>
            Examples
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...bySource.entries()].map(([source, rows]) =>
          rows.map((row, index) => (
            <TableRow key={`${source}-${row.grade}`}>
              <TableCell sx={{ pl: 0 }}>
                {index === 0 ? (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {source}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell>{row.grade}</TableCell>
              <TableCell align="right" sx={{ pr: 0 }}>
                {row.examples.toLocaleString()}
              </TableCell>
            </TableRow>
          )),
        )}
      </TableBody>
    </Table>
  );
}

export function DataHubBreakdown({ categories, grades }: BreakdownProps) {
  const measured = categories.reduce((total, row) => total + row.with_measurement, 0);

  return (
    <>
      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            By category
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {categories.length} kinds, {measured.toLocaleString()} with a grader&apos;s numbers
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {categories.length > 0 ? (
            <CategoryTable categories={categories} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Nothing categorised yet.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion variant="outlined" disableGutters>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            By grade
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            {grades.length} bands across the sources
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {grades.length > 0 ? (
            <GradeTable grades={grades} />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No graded examples yet.
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
}
